
import Vue, { PropType } from 'vue';
import { Timeline } from '@descomplica/vue-components';
import { CorrectionTime } from './PostGradTCCOrientations.vue';
import {
  Assessment,
  AssessmentResult,
  ASSESSMENT_RESULT_STATUS,
  Reply,
  REPLY_STATUS,
  REPLY_TYPE,
} from '@/models/assessment.model';
import { TabType, ThesisThemeItemType, ThesisThemeItem } from './types';
import PostGradTCCThemeSubmission from './PostGradTCCThemeSubmission.vue';
import PostGradTCCWaiting from './PostGradTCCWaiting.vue';
import PostGradTCCNextStep from './PostGradTCCNextStep.vue';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import { PostgradEarlyGraduationTrackingInformation } from '@/models/postgradEarlyGraduation/PostgradEarlyGraduationModel';

export default Vue.extend({
  components: {
    Timeline,
    PostGradTCCThemeSubmission,
    PostGradTCCWaiting,
    PostGradTCCNextStep,
  },
  props: {
    assessment: {
      type: Object as PropType<Assessment>,
      required: true,
    },
    dexCourseId: {
      type: Number,
      required: true,
    },
  },
  computed: {
    themeCorrectionTime(): number {
      return CorrectionTime.THEME;
    },
    assessmentResult(): AssessmentResult | undefined {
      const assessmentResultsByUser = this.assessment?.assessmentResultsByUser || [];
      return assessmentResultsByUser[0];
    },
    userName(): string {
      return this.$store.getters.currentAlumniProfile.firstName;
    },
    thesisThemeItems(): Array<ThesisThemeItem> {
      if (!this.assessmentResult || this.assessmentResult?.status === ASSESSMENT_RESULT_STATUS.NONE)
        return [
          {
            type: ThesisThemeItemType.firstTCCTheme,
            enabled: true,
            isLast: true,
          },
        ];

      const { replies } = this.assessmentResult;

      const sectionItems = replies
        .filter((reply: Reply) => reply.type === REPLY_TYPE.TCC_THEME)
        .reduce<ThesisThemeItem[]>(
          (thesisThemeItems: ThesisThemeItem[], reply: Reply, index: number) => {
            const items: ThesisThemeItem[] = [];

            if (index === 0) {
              items.push({
                type: ThesisThemeItemType.firstTCCTheme,
                enabled: false,
                data: {
                  themeText: reply.answer,
                },
                isLast: false,
              });
            }

            if (reply.status === REPLY_STATUS.PENDING) {
              items.push({
                type: ThesisThemeItemType.waitingReview,
                enabled: false,
                isLast: false,
              });
            } else if (reply.status === REPLY_STATUS.REJECTED) {
              const nextReply = replies[index + 1] || null;
              items.push({
                type: ThesisThemeItemType.themeRejected,
                enabled: !nextReply,
                data: {
                  themeText: nextReply && nextReply.answer,
                  feedbackText: reply.feedbackText,
                },
                isLast: false,
              });
            } else {
              items.push({
                type: ThesisThemeItemType.themeApproved,
                enabled: false,
                isLast: false,
              });
            }

            return thesisThemeItems.concat(items);
          },
          [],
        );

      if (sectionItems.length > 0) {
        sectionItems[sectionItems.length - 1].isLast = true;
      }

      return sectionItems;
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    earlyGraduationTrackingProps(): PostgradEarlyGraduationTrackingInformation {
      return this.$store.getters.earlyGraduationTrackings(this.currentChimeraCourse);
    },
  },
  methods: {
    async sendTccTheme(tccTheme: string) {
      try {
        this.$emit('update:loading', true);

        await this.$store.dispatch('SEND_TCC_THEME', {
          tccTheme,
          assessmentId: this.assessment.id,
          dexCourseId: this.dexCourseId,
        });

        await this.$store.dispatch('PULL_ASSESSMENT_TCC_DATA', {
          dexCourseId: this.dexCourseId,
          assessmentId: this.assessment.id,
        });

        this.trackEvents(tccTheme);
      } catch (_) {
        this.$emit(
          'displayToast',
          'error',
          this.$t('yourAssessments.tcc.tabs.thesisTheme.errorSendingTheme'),
        );
      } finally {
        this.$emit('update:loading', false);
      }
    },
    trackEvents(themeName: string): void {
      const assessmentResults = this.assessment.assessmentResultsByUser;
      const { replies } = assessmentResults[0];
      const relatedAssessmentCategory = this.assessment.category.name;

      const attemptNumber =
        assessmentResults.filter((ar) => ar.status === ASSESSMENT_RESULT_STATUS.FAILED).length + 1;

      if (replies.length === 0)
        Analytics.trackEvent(EVENT_LABELS.tccStart, {
          attemptNumber,
          relatedAssessmentCategory,
          ...this.earlyGraduationTrackingProps,
        });

      Analytics.trackEvent(EVENT_LABELS.tccSendTheme, {
        themeName,
        attemptNumber,
        relatedAssessmentCategory,
        ...this.earlyGraduationTrackingProps,
      });
    },
    goToThesisDevelopment() {
      this.$emit('selectTab', TabType.thesisDevelopment);
    },
  },
});
